<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    button-location="top"
    @submit="searchServiceOrders()"
  >
    <template #title>
      Home Service Orders
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <div class="col-sm-4">
          <label class="control-label">Search Type</label>
          <dropdown-list
            v-model="searchCondition.searchType"
            :options="searchTypeList"
          />
        </div>

        <div
          v-if="searchCondition.searchType"
          class="col-sm-8"
        >
          <template v-if="isPhoneNumberFieldVisible">
            <label class="control-label"> Phone Number* </label>
            <phone-number-input
              v-model="searchCondition.phoneNumber"
              :page-id="pageId"
            />
          </template>

          <template v-else-if="isOperatorFieldVisible">
            <label class="control-label"> Operator* </label>
            <dropdown-list
              v-model="searchCondition.operatorId"
              :options="homeServiceOperatorsList"
              required
              filterable
              clearable
            />
          </template>

          <template v-else>
            <label class="control-label">Search Value*</label>
            <sanitized-input
              v-model="searchCondition.searchValue"
              placeholder="Search..."
              required
            />
          </template>
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-4">
          <label class="control-label">Date Search Type</label>
          <dropdown-list
            v-model="searchCondition.dateSearchType"
            :options="dateSearchTypeList"
          />
        </div>

        <div class="col-sm-4">
          <label class="control-label pt-10"> From </label>
          <date-picker
            v-model="searchCondition.startDate"
            type="datetime"
            placeholder="mm/dd/yyyy hh:mm A"
            format="MM/DD/YYYY hh:mm A"
            :disabled-date="disabledStartDate"
          />
        </div>

        <div class="col-sm-4">
          <label class="control-label pt-10"> To </label>
          <date-picker
            v-model="searchCondition.endDate"
            type="datetime"
            placeholder="mm/dd/yyyy hh:mm A"
            format="MM/DD/YYYY hh:mm A"
            :disabled-date="disabledEndDate"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-4">
          <label class="control-label">Service Type</label>
          <dropdown-list
            v-model="searchCondition.serviceType"
            :options="serviceTypeList"
            clearable
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <label class="control-label"> Status </label>
          <div class="col-sm-12">
            <el-checkbox-group v-model="searchCondition.status">
              <div class="row mb-0">
                <div
                  v-for="(value, index) in Object.values(HomeServiceOrderStatus)"
                  :key="index"
                  class="col-sm-3 checkbox-container"
                >
                  <el-checkbox :label="value">
                    {{ startCase(value) }}
                  </el-checkbox>
                </div>
              </div>
            </el-checkbox-group>
          </div>
        </div>
      </div>

      <div class="form-submit-button-group">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isSubmitBtnDisabled"
        >
          Search
        </p-button>

        <p-button
          type="purple"
          round
          :disabled="isSubmitBtnDisabled"
          @click="onSearchScheduledFollowUps"
        >
          Scheduled Follow-ups
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center mt-4">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="searchServiceOrders"
        />
      </div>
    </template>

    <template #content>
      <div class="custom-collapse">
        <el-collapse
          v-model="activeCollapseId"
          accordion
        >
          <HomeServiceOrderCardItem
            v-for="item in homeServiceOrders"
            :key="item.id"
            :order-item="item"
            :is-active="activeCollapseId === item.id"
            @reload="() => searchServiceOrders(status.currentPage)"
          />
        </el-collapse>
      </div>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, Ref, watch, defineAsyncComponent, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { startCase, orderBy } from 'lodash';
import moment from 'moment-timezone';

import util from '@/Utils/util';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { useGlobal } from '@/use/global';
import { useCustomer } from '@/use/customer';
import { useHomeService } from '@/use/homeService';
import { HomeServiceOrderStatus, HomeServiceOrder, type HomeServiceType, QuerySortType } from '@/models/homeService';
import { ListType } from '@/models/app';
import { homeServiceOrderService } from '@/services/homeServiceOrder';
import { operatorService } from '@/services/operators';

const HomeServiceOrderCardItem = defineAsyncComponent(() => import('./Order/HomeServiceOrderCardItem.vue') as any);

enum HomeServiceSearchType {
  Any = '',
  CallerPhone = 'callerPhone',
  CallerId = 'callerId',
  OrderId = 'id',
  ProviderId = 'providerId',
  OperatorId = 'operatorId',
  OperatorSlackId = 'operatorSlackId',
}

const pageId = 'home-service-orders';
const paginationLimit = 10;
const operatorPaginationLimit = 100;
const searchTypeList = [
  { id: 1, key: HomeServiceSearchType.Any, label: 'Any' },
  { id: 2, key: HomeServiceSearchType.CallerPhone, label: 'Caller Phone' },
  { id: 3, key: HomeServiceSearchType.CallerId, label: 'Caller ID' },
  { id: 4, key: HomeServiceSearchType.OrderId, label: 'Order ID' },
  { id: 5, key: HomeServiceSearchType.ProviderId, label: 'Provider ID' },
  { id: 6, key: HomeServiceSearchType.OperatorId, label: 'Operator' },
  { id: 7, key: HomeServiceSearchType.OperatorSlackId, label: 'Operator Slack ID' },
];
const dateSearchTypeList = [
  { id: 1, key: 'startAt', label: 'Service Date' },
  { id: 2, key: 'nextFollowUpDueAt', label: 'Follow-up Call Date/Time' },
  { id: 3, key: 'createdAt', label: 'Created Date' },
];

const route = useRoute();

const { customerPhoneNumber } = useCustomer();
const { serviceTypeList } = useHomeService();
const { lastFormData, setFormData } = useGlobal(pageId);

const homeServiceOrders: Ref<HomeServiceOrder[]> = ref([]);
const searchCondition = ref({
  searchType: HomeServiceSearchType.Any,
  phoneNumber: '',
  serviceType: '',
  searchValue: '',
  operatorId: '',
  dateSearchType: dateSearchTypeList[0].key,
  startDate: null,
  endDate: null,
  status: [] as HomeServiceOrderStatus[],
});
const homeServiceOperatorsList: Ref<ListType[]> = ref([]);

const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  totalPages: 1,
  currentPage: 1,
});
const activeCollapseId = ref(0);

const isPhoneNumberFieldVisible = computed(() => searchCondition.value.searchType === HomeServiceSearchType.CallerPhone);
const isOperatorFieldVisible = computed(() => searchCondition.value.searchType === HomeServiceSearchType.OperatorId);
const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(searchCondition.value.phoneNumber));

const isSubmitBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (isPhoneNumberFieldVisible.value && !isPhoneNumberValid.value) {
    return true;
  }

  return false;
});

const orderByField = computed(() => searchCondition.value.status.includes(HomeServiceOrderStatus.FollowingUp) ? 'nextFollowUpDueAt' : 'createdAt');
const sortType = computed(() => searchCondition.value.status.includes(HomeServiceOrderStatus.FollowingUp) ? QuerySortType.Asc : QuerySortType.Desc);

const disabledStartDate = (date: Date) => {
  if (searchCondition.value.endDate && util.formatDateTime(date) > util.formatDateTime(searchCondition.value.endDate)) {
    return true;
  }

  return false;
};

const disabledEndDate = (date: Date) => {
  if (searchCondition.value.startDate && util.formatDateTime(date) < util.formatDateTime(searchCondition.value.startDate)) {
    return true;
  }

  return false;
};

const searchServiceOrders = (currentPage: number = 1) => {
  status.currentPage = currentPage;
  const offset = (currentPage - 1) * paginationLimit;

  let searchValue: string | null;
  if (isPhoneNumberFieldVisible.value) {
    searchValue = searchCondition.value.phoneNumber;
  } else if (isOperatorFieldVisible.value) {
    searchValue = searchCondition.value.operatorId || '';
  } else {
    searchValue = searchCondition.value.searchValue;
  }

  const startDate = searchCondition.value.startDate && util.formatDateTime(searchCondition.value.startDate, 'YYYY-MM-DD HH:mm');
  const endDate = searchCondition.value.endDate && util.formatDateTime(searchCondition.value.endDate, 'YYYY-MM-DD HH:mm');

  const payload = {
    searchType: searchCondition.value.searchType,
    searchValue,
    dateSearchType: searchCondition.value.dateSearchType,
    dateSearchValue: {
      startDate,
      endDate,
    },
    status: searchCondition.value.status,
    serviceType: searchCondition.value.serviceType as HomeServiceType || null,
    orderBy: orderByField.value,
    sortType: sortType.value,
    pagination: {
      limit: paginationLimit,
      offset,
    },
  };

  status.spinnerMessage = 'Loading Home Service Orders...';
  status.isLoading = true;

  return catchErrorMessageBox(async () => {
    const response = await homeServiceOrderService.getHomeServiceOrders(payload);

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    homeServiceOrders.value = response.rows;
    activeCollapseId.value = 0;
  })
    .finally(() => (status.isLoading = false))
    .catch(() => {
      status.totalPages = 1;
      status.currentPage = 1;
    });
};

const onSearchScheduledFollowUps = () => {
  searchCondition.value = {
    ...searchCondition.value,
    dateSearchType: 'nextFollowUpDueAt',
    startDate: moment.utc().toDate(),
    status: [HomeServiceOrderStatus.Duplicate, HomeServiceOrderStatus.FollowingUp, HomeServiceOrderStatus.Interviewing, HomeServiceOrderStatus.Pending, HomeServiceOrderStatus.Scheduled, HomeServiceOrderStatus.Recurring],
  };

  searchServiceOrders();
};

const loadAllHomeServiceOperators = async () => {
  status.spinnerMessage = 'Loading Home Service Operators...';
  status.isLoading = true;

  await loadHomeServiceOperators();

  homeServiceOperatorsList.value = orderBy(homeServiceOperatorsList.value, ['label'], ['asc']);
  homeServiceOperatorsList.value.unshift({
    id: '',
    key: '',
    label: '- Unassigned -',
  });
  status.isLoading = false;
};

const loadHomeServiceOperators = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * operatorPaginationLimit;
  try {
    const response = await operatorService.fetchOperators({
      roles: ['homeServices-operator'],
      isActive: true,
      pagination: {
        offset,
        limit: operatorPaginationLimit,
      },
    });

    for (const operator of response.rows) {
      homeServiceOperatorsList.value.push({
        id: operator.id,
        key: operator.id,
        label: `${operator.firstName} ${operator.lastName}`,
      });
    }

    if (homeServiceOperatorsList.value.length < response.count) {
      return loadHomeServiceOperators(currentPage + 1);
    }
  } catch (err) {
    // No need to do anything
  }
};

const resetForm = () => {
  searchCondition.value = {
    searchType: customerPhoneNumber.value ? HomeServiceSearchType.CallerPhone : HomeServiceSearchType.Any,
    phoneNumber: customerPhoneNumber.value,
    serviceType: '',
    searchValue: '',
    operatorId: '',
    dateSearchType: dateSearchTypeList[0].key,
    startDate: null,
    endDate: null,
    status: [],
  };

  status.currentPage = 1;
  status.totalPages = 1;

  homeServiceOrders.value = [];
};

watch(customerPhoneNumber, (value: string) => {
  resetForm();

  if (value) {
    searchServiceOrders();
  }
});

onMounted(async () => {
  await loadAllHomeServiceOperators();

  const orderId = (route.query.orderId as string) ?? '';

  if (orderId) {
    searchCondition.value.searchType = HomeServiceSearchType.OrderId;
    searchCondition.value.searchValue = orderId;
  } else if (lastFormData.value) {
    searchCondition.value = lastFormData.value;
    status.currentPage = lastFormData.value.currentPage ?? 1;
  } else if (customerPhoneNumber.value) {
    searchCondition.value.searchType = HomeServiceSearchType.CallerPhone;
    searchCondition.value.phoneNumber = customerPhoneNumber.value;
  }

  if (searchCondition.value.searchType !== HomeServiceSearchType.Any) {
    searchServiceOrders(status.currentPage);
  }
});

onBeforeUnmount(() => {
  setFormData({
    ...searchCondition.value,
    currentPage: status.currentPage,
  });
});
</script>
